import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { UIButton, UISubNav, useUINav } from '@/ui';
import { CompanyUsersList } from '@/companies/components/company-users-list';
import { useFetchUserCounts } from '@/companies/queries';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';
import { Navigate } from 'react-router';
import { InviteUsersModal } from '@/companies/components/invite-users-modal';
import { useCompanyContext } from '@/companies/providers';
import { LimitedViewBanner } from '@/companies/components';

export function MembersSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            {
                text: t('shared.labels.settings')
            },
            { text: t('shared.labels.members') }
        ]);
    }, [setParts, t]);

    const { canViewMembers, canEditMembers } = useCompanyPermissions();

    const [isInviteUsersModelOpen, setIsInviteUsersModelOpen] = useState(false);

    const rightNode = useCallback(() => {
        if (!canEditMembers) {
            return undefined;
        }
        return (
            <UIButton
                variant="primary"
                text={t('pages.members_settings.invite')}
                leftIcon={'plus-circle'}
                onClick={() => setIsInviteUsersModelOpen(true)}
            />
        );
    }, [canEditMembers, t]);

    const { company } = useCompanyContext();
    const { counts } = useFetchUserCounts(company.id);

    const subtitle = useMemo(() => {
        // Nothing to say until the counts land — better than flashing
        // "0 members".
        if (!counts) {
            return undefined;
        }

        const countText = `${counts.total} ${
            counts.total === 1 ? 'member' : 'members'
        }`;
        if (
            company.subscription &&
            ['active', 'past_due'].includes(company.subscription.status)
        ) {
            return `${countText} · ${counts.assignedLicense}/${company.subscription.quantity} licenses assigned`;
        }
        return countText;
    }, [counts, company]);

    if (!canViewMembers) {
        return <Navigate to="/" replace />;
    }

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    padding: 32,
                    gap: 24
                }}
            >
                {!canEditMembers && <LimitedViewBanner />}
                <UISubNav
                    title={t('shared.labels.members')}
                    subTitle={subtitle}
                    rightItems={rightNode()}
                />
                <CompanyUsersList />
            </div>
            <InviteUsersModal
                isOpen={isInviteUsersModelOpen}
                onClose={() => setIsInviteUsersModelOpen(false)}
            />
        </>
    );
}
